'use client';

import React, { useState } from 'react';
import { reportError } from '@/lib/utils/error-notification';
import WebsiteUrlInput from '@/components/forms/WebsiteUrlInput';
import { useService } from '@/lib/service-context';
import { trackPostHogEvent } from '@/lib/utils/dataLayer';

// Specific spam markers only — avoid generic terms like "seo" that block real leads.
const BLACKLIST_WORDS = [
  'seoexpert',
  'asd@asd.com',
  'joinrunway',
  'reliabletaskassist',
  'virtualassistant',
];

function containsBlacklistedWords(text: string): boolean {
  if (!text) return false;
  const lowerText = text.toLowerCase();
  return BLACKLIST_WORDS.some((word) => lowerText.includes(word.toLowerCase()));
}

interface AuditFormProps {
  layout?: 'column' | 'row';
  className?: string;
  buttonText?: string;
  ctaText?: string;
  /** When set, overrides ServiceContext for submissions and success copy (e.g. toolkit tool name). */
  serviceName?: string;
  /** Email notification source label (default: homepage-audit-form). */
  formSource?: string;
  /** Initial value for the “tell us more” field; form remount with key when tool changes. */
  initialFocusArea?: string;
  /** Placeholder for the details / focus textarea (e.g. toolkit hire modal). */
  focusAreaPlaceholder?: string;
  serviceType?: 'quick' | 'monthly';
  stackFields?: boolean;
  /** When false, website is optional (default true). */
  requireWebsite?: boolean;
  onSubmit?: (data: { name: string; email: string; website: string }) => void;
  onServiceChange?: (serviceName: string) => void;
}

const AuditForm: React.FC<AuditFormProps> = ({
  layout = 'column',
  className = '',
  buttonText = 'Send Me My Audit & Plan',
  ctaText = 'Continue',
  serviceName: serviceNameProp,
  formSource,
  initialFocusArea = '',
  focusAreaPlaceholder = 'Tell me more about your project',
  serviceType = 'monthly',
  stackFields = false,
  requireWebsite = true,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [focusArea, setFocusArea] = useState(initialFocusArea);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { selectedService } = useService();
  const currentServiceName =
    (serviceNameProp && serviceNameProp.trim()) ? serviceNameProp.trim() : (selectedService || 'consultation');
  const resolvedSource = formSource ?? 'homepage-audit-form';
  
  // Validation states
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    website?: string;
    focusArea?: string;
  }>({});
  
  // Validation functions
  const validateName = (name: string): string | undefined => {
    const trimmed = name.trim();
    if (!trimmed) return 'Name is required';
    
    // Check for blacklisted words
    if (containsBlacklistedWords(trimmed)) {
      return 'Please use a different name';
    }
    
    if (trimmed.length < 2) {
      return 'Please enter your name';
    }
    if (/\d/.test(trimmed)) {
      return 'Name cannot contain numbers';
    }
    return undefined;
  };
  
  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) return 'Email is required';
    
    // Check for blacklisted words
    if (containsBlacklistedWords(email)) {
      return 'Please use a different email address';
    }
    
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    
    return undefined;
  };
  
  const validateWebsite = (website: string): string | undefined => {
    if (!website.trim()) {
      return requireWebsite ? 'Website URL is required' : undefined;
    }
    
    // Check for at least one dot (domain separator)
    if (!website.includes('.')) {
      return 'Website URL must contain a domain (e.g., example.com)';
    }
    
    // Add protocol if missing
    let urlToValidate = website;
    if (!urlToValidate.startsWith('http://') && !urlToValidate.startsWith('https://')) {
      urlToValidate = 'https://' + urlToValidate;
    }
    
    try {
      new URL(urlToValidate);
      return undefined;
    } catch {
      return 'Please enter a valid website URL';
    }
  };

  const validateFocusArea = (focusArea: string): string | undefined => {
    if (!focusArea.trim()) return 'Please let us know what to focus on first';
    if (focusArea.trim().length < 10) return 'Please provide more details about your focus area';
    return undefined;
  };
  
  const clearErrors = () => {
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    setSubmitError(null);
    
    // Validate all fields before submission
    const nameError = validateName(name);
    const emailError = validateEmail(email);
    const websiteError = validateWebsite(website);
    const focusAreaError = validateFocusArea(focusArea);
    
    if (nameError || emailError || websiteError || focusAreaError) {
      setErrors({ name: nameError, email: emailError, website: websiteError, focusArea: focusAreaError });
      return;
    }
    
    setIsSubmitting(true);

    const formData = {
      name: name.trim(),
      email: email.trim(),
      website: website.trim(),
      focusArea: focusArea.trim(),
      serviceName: currentServiceName,
      serviceType: serviceType,
      servicePrice: 0,
      source: resolvedSource,
    };
    
    try {
      const notificationResponse = await fetch('/api/form-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!notificationResponse.ok) {
        console.warn('Form notification failed:', notificationResponse.status);
        reportError(new Error('Form notification failed'), {
          custom: {
            status: notificationResponse.status,
            statusText: notificationResponse.statusText,
            formData: {
              name: formData.name,
              email: formData.email,
              website: formData.website,
              focusArea: formData.focusArea,
              serviceName: currentServiceName,
              source: resolvedSource,
            },
          },
        }, 'warning');
        setSubmitError('Something went wrong sending your request. Please try again.');
        setIsSubmitted(false);
        return;
      }

      trackPostHogEvent('form_submitted', {
        form_source: resolvedSource,
        service_name: currentServiceName,
        has_website: Boolean(formData.website),
        focus_area: formData.focusArea,
      });

      setIsSubmitted(true);
      setName('');
      setEmail('');
      setWebsite('');
      setFocusArea('');

      if (onSubmit) {
        onSubmit({ name: formData.name, email: formData.email, website: formData.website });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      reportError(new Error('Form submission network error'), {
        custom: {
          error: error instanceof Error ? error.message : String(error),
          selectedService: currentServiceName,
          formData: { name, email, website, source: resolvedSource },
        },
      });
      setSubmitError('Something went wrong sending your request. Please try again.');
      setIsSubmitted(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isInline = layout === 'row';

  if (isInline) {
    if (isSubmitted) {
      return (
        <div className={`text-center space-y-4 ${className}`}>
          <div className="text-green-600 text-4xl">✓</div>
          <h3 className="text-lg font-bold text-gray-900">Thank You!</h3>
          <p className="text-gray-600">Your {currentServiceName} request has been submitted successfully. We'll be in touch within couple of hours.</p>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <input
              type="text"
              name="name"
              placeholder="Full name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  const error = validateName(e.target.value);
                  if (!error) {
                    setErrors(prev => ({ ...prev, name: undefined }));
                  }
                }
              }}
              className={`w-full px-4 py-3 text-base font-medium text-gray-900 placeholder-gray-500 border rounded-lg focus:ring-gray-900 focus:border-gray-900 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>
          <div className="flex-1">
            <input
              type="email"
              name="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // Clear errors when user starts typing
                if (errors.email) {
                  setErrors(prev => ({ ...prev, email: undefined }));
                }
              }}
              className={`w-full px-4 py-3 text-base font-medium text-gray-900 placeholder-gray-500 border rounded-lg focus:ring-gray-900 focus:border-gray-900 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>
          <div className="flex-1">
            <div className={errors.website ? '[&>div>div]:border-red-500' : ''}>
              <WebsiteUrlInput
                id="website"
                label=""
                value={website}
                placeholder="example.com"
                onChange={(fullUrl) => {
                  setWebsite(fullUrl);
                  if (errors.website) {
                    const error = validateWebsite(fullUrl);
                    if (!error) {
                      setErrors(prev => ({ ...prev, website: undefined }));
                    }
                  }
                }}
              />
            </div>
            {errors.website && (
              <p className="mt-1 text-sm text-red-600">{errors.website}</p>
            )}
          </div>
        </div>
        <div className="w-full pt-2">
          <textarea
            name="focusArea"
            placeholder={focusAreaPlaceholder}
            value={focusArea}
            onChange={(e) => {
              setFocusArea(e.target.value);
              if (errors.focusArea) {
                const error = validateFocusArea(e.target.value);
                if (!error) {
                  setErrors(prev => ({ ...prev, focusArea: undefined }));
                }
              }
            }}
            rows={3}
            className={`w-full px-4 py-3 text-base font-medium text-gray-900 placeholder-gray-500 border rounded-lg focus:ring-gray-900 focus:border-gray-900 ${
              errors.focusArea ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.focusArea && (
            <p className="mt-1 text-sm text-red-600">{errors.focusArea}</p>
          )}
        </div>
        {submitError && (
          <p className="text-sm text-red-600 text-center">{submitError}</p>
        )}
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto px-8 py-3 text-base font-bold text-white transition-all duration-200 bg-gray-900 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 border border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : ctaText}
          </button>
        </div>
      </form>
    );
  }

  // If form is submitted, show success message instead of form
  if (isSubmitted) {
    return (
      <div className={className}>
        <div className="text-center space-y-6 py-8">
          <div className="text-green-600 text-6xl">✓</div>
          <h3 className="text-2xl font-bold text-gray-900">Thank You!</h3>
          <p className="text-lg text-gray-600 max-w-md mx-auto">Your {currentServiceName} request has been submitted successfully. We'll be in touch within a couple of hours.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={className}>
          <div className="space-y-4">
        {/* Name and Email — stacked or side by side depending on context */}
        <div className={`grid grid-cols-1 gap-4 ${stackFields ? '' : 'sm:grid-cols-2'}`}>
            <div>
              <label htmlFor="name" className="sr-only">
                Full name
              </label>
              <input 
                type="text" 
                name="name" 
                id="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) {
                    const error = validateName(e.target.value);
                    if (!error) {
                      setErrors(prev => ({ ...prev, name: undefined }));
                    }
                  }
                }}
                className={`block w-full px-4 py-3 text-base sm:py-3.5 sm:text-sm font-medium text-gray-900 placeholder-gray-500 border rounded-lg focus:ring-gray-900 focus:border-gray-900 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Full name" />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

          <div>
            <label htmlFor="email" className="sr-only">
              Email address
            </label>
            <input 
              type="email" 
              name="email" 
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) {
                  setErrors(prev => ({ ...prev, email: undefined }));
                }
              }}
              className={`block w-full px-4 py-3 text-base sm:py-3.5 sm:text-sm font-medium text-gray-900 placeholder-gray-500 border rounded-lg focus:ring-gray-900 focus:border-gray-900 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Your email" />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>
        </div>

        {/* Website URL */}
            <div>
              <label htmlFor="website" className="sr-only">
                Website URL
              </label>
              <div className={errors.website ? '[&>div>div]:border-red-500' : ''}>
                <WebsiteUrlInput
                  id="website"
                  label=""
                  value={website}
                  placeholder="example.com"
                  onChange={(fullUrl) => {
                    setWebsite(fullUrl);
                    if (errors.website) {
                      const error = validateWebsite(fullUrl);
                      if (!error) {
                        setErrors(prev => ({ ...prev, website: undefined }));
                      }
                    }
                  }}
                />
              </div>
              {errors.website && (
                <p className="mt-1 text-sm text-red-600">{errors.website}</p>
              )}
            </div>

        {/* Details field */}
            <div>
              <label htmlFor="focusArea" className="sr-only">
                Focus Area
              </label>
              <textarea 
                name="focusArea" 
                id="focusArea"
                value={focusArea}
                onChange={(e) => {
                  setFocusArea(e.target.value);
                  if (errors.focusArea) {
                    const error = validateFocusArea(e.target.value);
                    if (!error) {
                      setErrors(prev => ({ ...prev, focusArea: undefined }));
                    }
                  }
                }}
                rows={3}
                className={`block w-full px-4 py-3 text-base sm:py-3.5 sm:text-sm font-medium text-gray-900 placeholder-gray-500 border rounded-lg focus:ring-gray-900 focus:border-gray-900 ${
                  errors.focusArea ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={focusAreaPlaceholder} />
              {errors.focusArea && (
                <p className="mt-1 text-sm text-red-600">{errors.focusArea}</p>
              )}
            </div>

        {submitError && (
          <p className="text-sm text-red-600">{submitError}</p>
        )}

        {/* Submit button */}
            <div className="relative group">
              <div className="absolute transition-all duration-1000 opacity-70 -inset-px bg-gradient-to-r from-[#13479c] via-[#0f3a7a] to-[#0a2d5c] rounded-xl blur-lg filter group-hover:opacity-100 group-hover:duration-200"></div>
              <button 
                type="submit"
                disabled={isSubmitting}
                className="relative inline-flex items-center justify-center w-full px-8 py-3 text-base sm:py-3.5 font-bold text-white transition-all duration-200 bg-[#13479c] rounded-lg sm:text-sm hover:bg-[#0f3a7a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#13479c] border border-transparent disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? 'Submitting...' : buttonText}
              </button>
            </div>
          </div>
    </form>
  );
};

export default AuditForm;
